import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type {
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community'
import type { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { isEqual } from 'lodash'
import type { Value } from '@skop-app/lib-domain/types'
import { useTranslations } from 'next-intl'

import type {
  ColumnStateConfig,
  ExternalFilters,
  ExternalFiltersConfig,
  GridApiContextValue,
  GridContextValue,
  GridRootProps,
  GridStateContextValue,
  GridTableContextValue,
  GridViewsContextValue,
} from '../types'
import { useColumnStatePersistence } from '../persistence/useColumnStatePersistence'
import { useExternalFiltersPersistence } from '../persistence/useExternalFiltersPersistence'
import { useShowRowGroupOnFirstColumn } from '../persistence/useShowRowGroupOnFirstColumn'
import { sideBarDef } from '../grid-options'
import { buildScopeKey } from '../storage'
import { clearActiveGrid, setActiveGrid } from '../activeGrid'
import { createRowClassFn } from '../utils'
import { useLatestRef } from '@/hooks/useLatestRef'
import { isRecord } from '@/utils/typeGuards'
import { extractScalarValue } from '@/utils/value.utils'
import { useGridViewsManager } from '../views/useGridViewsManager'
import {
  projectGridViewColumnDefs,
  stableSerializeGridViewPayload,
} from '@/components/Grid/views/payload'

const GridApiContext = createContext<GridApiContextValue | null>(null)
const GridStateContext = createContext<GridStateContextValue | null>(null)
const GridTableContext = createContext<GridTableContextValue<
  unknown,
  unknown
> | null>(null)
const GridViewsContext = createContext<
  GridViewsContextValue | null | undefined
>(undefined)
const NOOP_UNSUBSCRIBE = () => {}

type GridViewsProviderProps<Row, Ctx> = {
  children: React.ReactNode
  config: NonNullable<GridRootProps<Row, Ctx>['features']>['views']
  scope: GridRootProps<Row, Ctx>['scope']
  api: GridApi<Row> | null
  gridIsReady: boolean
  columnDefsReady: boolean
  columnDefs: AgGridReactProps<Row>['columnDefs']
  columnStateConfig?: ColumnStateConfig
  externalFiltersConfig?: ExternalFiltersConfig<Row, Ctx>
  externalFiltersSelected: ExternalFilters
  setExternalFiltersSelected: GridStateContextValue['setExternalFiltersSelected']
  tableContextValue: GridTableContextValue<Row, Ctx>
  showRowGroupOnFirstColumn: boolean
}

function GridViewsProvider<Row, Ctx>({
  children,
  config,
  scope,
  api,
  gridIsReady,
  columnDefsReady,
  columnDefs,
  columnStateConfig,
  externalFiltersConfig,
  externalFiltersSelected,
  setExternalFiltersSelected,
  tableContextValue,
  showRowGroupOnFirstColumn,
}: GridViewsProviderProps<Row, Ctx>) {
  const t = useTranslations()
  const gridViews = useGridViewsManager({
    config,
    scope,
    api,
    gridIsReady,
    columnDefsReady,
    columnDefs,
    columnStateConfig,
    externalFiltersConfig,
    externalFiltersSelected,
    setExternalFiltersSelected,
  })

  const viewBootstrapKey = useMemo(
    () =>
      [
        scope.view,
        scope.orgId ?? '',
        scope.teamId ?? '',
        scope.projectId ?? '',
        config?.scopeKey ?? '',
        config?.gridSchemaVersion ?? '',
      ].join(':'),
    [
      config?.gridSchemaVersion,
      config?.scopeKey,
      scope.orgId,
      scope.projectId,
      scope.teamId,
      scope.view,
    ],
  )
  const [initialExternalFiltersApplied, setInitialExternalFiltersApplied] =
    useState<{
      bootstrapKey: string
      signature: string
    } | null>(null)
  const [releasedBootstrapState, setReleasedBootstrapState] = useState<{
    bootstrapKey: string
    signature: string
    initialGridState: GridViewsContextValue['initialGridState']
  } | null>(null)

  const hasReleasedInitialMount =
    releasedBootstrapState?.bootstrapKey === viewBootstrapKey
  const hasAppliedInitialExternalFilters =
    initialExternalFiltersApplied?.bootstrapKey === viewBootstrapKey &&
    initialExternalFiltersApplied.signature === gridViews?.initialStateSignature
  const initialExternalFiltersReady = hasAppliedInitialExternalFilters
  const shouldDelayGridMount = Boolean(
    gridViews?.enabled && !hasReleasedInitialMount,
  )

  useEffect(() => {
    if (!gridViews?.isInitialStateReady || !gridViews.initialStateSignature) {
      return
    }
    if (hasReleasedInitialMount) return
    if (hasAppliedInitialExternalFilters) return

    setExternalFiltersSelected((current) =>
      isEqual(current, gridViews.initialExternalFilters)
        ? current
        : gridViews.initialExternalFilters,
    )
    setInitialExternalFiltersApplied({
      bootstrapKey: viewBootstrapKey,
      signature: gridViews.initialStateSignature,
    })
  }, [
    gridViews?.initialExternalFilters,
    gridViews?.initialStateSignature,
    gridViews?.isInitialStateReady,
    hasAppliedInitialExternalFilters,
    hasReleasedInitialMount,
    setExternalFiltersSelected,
    viewBootstrapKey,
  ])

  useEffect(() => {
    if (!gridViews?.enabled) return
    if (!gridViews.isInitialStateReady || !initialExternalFiltersReady) return
    const initialStateSignature = gridViews.initialStateSignature
    if (!initialStateSignature) return

    setReleasedBootstrapState((current) =>
      current?.bootstrapKey === viewBootstrapKey
        ? current
        : {
            bootstrapKey: viewBootstrapKey,
            signature: initialStateSignature,
            initialGridState: gridViews.initialGridState,
          },
    )
  }, [
    gridViews?.enabled,
    gridViews?.initialGridState,
    gridViews?.initialStateSignature,
    gridViews?.isInitialStateReady,
    initialExternalFiltersReady,
    viewBootstrapKey,
  ])

  const currentGridViewPayload = gridViews?.isInitialStateReady
    ? gridViews.initialGridViewPayload
    : undefined
  // Filtering changes the view payload, but only columns, sort and sizing
  // can change the projected definitions. Keep AG Grid filter menus mounted.
  const columnProjectionSignature = useMemo(
    () =>
      currentGridViewPayload
        ? stableSerializeGridViewPayload({
            ...currentGridViewPayload,
            filterModel: {},
            externalFilters: {},
          })
        : null,
    [currentGridViewPayload],
  )
  const projectedColumnDefsBeforeGroupHost = useMemo(
    () =>
      currentGridViewPayload
        ? projectGridViewColumnDefs({
            columnDefs: tableContextValue.agGridProps.columnDefs,
            payload: currentGridViewPayload,
          })
        : tableContextValue.agGridProps.columnDefs,
    /*
     * The payload object can be rehydrated under a new reference while its
     * normalized content is unchanged. Column projection must follow the
     * normalized signature, otherwise AG Grid receives equivalent columnDefs and
     * rebuilds tree columns during ordinary row refreshes.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnProjectionSignature, tableContextValue.agGridProps.columnDefs],
  )
  const projectedColumnDefs = useShowRowGroupOnFirstColumn<Row>({
    api,
    columnDefs: projectedColumnDefsBeforeGroupHost,
    enabled: showRowGroupOnFirstColumn,
  })

  const initialGridState =
    releasedBootstrapState?.bootstrapKey === viewBootstrapKey &&
    releasedBootstrapState.signature === gridViews?.initialStateSignature
      ? releasedBootstrapState.initialGridState
      : undefined

  const tableContextValueWithInitialState = useMemo(() => {
    return {
      ...tableContextValue,
      isInteractionBlocked: gridViews?.isInteractionBlocked === true,
      agGridProps:
        initialGridState || columnProjectionSignature
          ? {
              ...tableContextValue.agGridProps,
              columnDefs: projectedColumnDefs,
              ...(initialGridState ? { initialState: initialGridState } : {}),
            }
          : tableContextValue.agGridProps,
    }
  }, [
    columnProjectionSignature,
    gridViews?.isInteractionBlocked,
    initialGridState,
    projectedColumnDefs,
    tableContextValue,
  ])

  return (
    <GridViewsContext.Provider value={gridViews}>
      <GridTableContext.Provider value={tableContextValueWithInitialState}>
        {shouldDelayGridMount ? (
          <div className="flex h-full min-h-40 items-center justify-center text-sm text-gray-500">
            {t('GridView.loadingView')}
          </div>
        ) : (
          children
        )}
      </GridTableContext.Provider>
    </GridViewsContext.Provider>
  )
}

const isValue = (value: unknown): value is Value =>
  typeof value === 'string' || typeof value === 'number'

const toValueArray = (value: unknown): Value[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => extractScalarValue(item))
      .filter((item): item is Value => item !== undefined)
  }
  const extracted = extractScalarValue(value)
  return extracted === undefined ? [] : [extracted]
}

const getValueByPath = (data: unknown, path: string): unknown => {
  if (!data || typeof data !== 'object') return undefined
  if (!path) return undefined
  if (!path.includes('.')) {
    return (data as Record<string, unknown>)[path]
  }
  return path.split('.').reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== 'object') return undefined
    const record = acc as Record<string, unknown>
    return record[key]
  }, data)
}

const matchesValues = (rowValues: Value[], selectedValues: Value[]) => {
  if (selectedValues.length === 0) return true
  if (rowValues.length === 0) return selectedValues.includes('')
  return rowValues.some((value) => selectedValues.includes(value))
}

export function GridRoot<Row, Ctx>({
  children,
  scope,
  agGridProps,
  gridContext,
  getContextMenuItems,
  features,
}: GridRootProps<Row, Ctx>) {
  const gridRef = useRef<AgGridReact | null>(null)
  const [gridIsReady, setGridIsReady] = useState(false)
  const [gridApi, setGridApi] = useState<GridApi<Row> | null>(null)
  const [columnsPanelRequested, setColumnsPanelRequested] = useState(false)
  const scopeKey = useMemo(() => buildScopeKey(scope), [scope])
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, unknown>
  >({})
  const [quickSearchValue, setQuickSearchValue] = useState('')
  const quickSearchValueRef = useLatestRef(quickSearchValue)

  const externalFiltersConfig = features?.externalFilters
  const columnStateConfig = features?.columnState
  const viewsConfig = features?.views
  const treeDataConfig = features?.treeData
  const rulesConfig = features?.rules
  const aggregationConfig = features?.aggregation
  const contextRefreshConfig = features?.contextRefresh
  const viewsEnabled = Boolean(viewsConfig && viewsConfig.enabled !== false)
  const columnDefsReady =
    Array.isArray(agGridProps.columnDefs) && agGridProps.columnDefs.length > 0

  const persistedExternalFiltersConfig = useMemo(() => {
    if (!externalFiltersConfig) return externalFiltersConfig
    if (!viewsEnabled) return externalFiltersConfig
    return {
      ...externalFiltersConfig,
      persist: false,
    }
  }, [externalFiltersConfig, viewsEnabled])

  const persistedColumnStateConfig = useMemo(() => {
    if (!columnStateConfig) return columnStateConfig
    if (!viewsEnabled) return columnStateConfig
    return {
      ...columnStateConfig,
      persist: false,
    }
  }, [columnStateConfig, viewsEnabled])

  const {
    externalFiltersSelected,
    setExternalFiltersSelected,
    externalFiltersStorageKey,
    resetExternalFilters,
  } = useExternalFiltersPersistence({
    config: persistedExternalFiltersConfig,
    scope,
  })

  useColumnStatePersistence({
    config: persistedColumnStateConfig,
    scope,
    api: gridApi,
    gridIsReady,
    columnDefsReady,
  })

  const externalFilterStateRef = useLatestRef({
    externalFiltersConfig,
    externalFiltersSelected,
    gridContext,
  })

  useEffect(() => {
    if (!externalFiltersConfig) return
    gridApi?.onFilterChanged()
  }, [externalFiltersConfig, externalFiltersSelected, gridApi])

  useEffect(() => {
    setActiveGrid({ scopeKey, gridRef, gridIsReady })
    return () => {
      clearActiveGrid(scopeKey)
    }
  }, [gridIsReady, scopeKey])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.code !== 'Escape') return
      if (!gridApi) return

      const selectedNodes = gridApi.getSelectedNodes()
      const selectedNodesList = Array.isArray(selectedNodes)
        ? selectedNodes
        : []
      if (selectedNodesList.length > 0) {
        gridApi.setNodesSelected({ nodes: selectedNodesList, newValue: false })
      }

      gridApi.clearCellSelection()
      gridApi.setSideBarVisible(false)
      gridApi.closeToolPanel()
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [gridApi])

  const onFilterChangedProp = agGridProps.onFilterChanged
  const onGridReadyProp = agGridProps.onGridReady

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent<Row>) => {
      const nextFilters = event.api.getFilterModel() as Record<string, unknown>
      setSelectedFilters((prev) =>
        isEqual(prev, nextFilters) ? prev : nextFilters,
      )
      onFilterChangedProp?.(event)
    },
    [onFilterChangedProp],
  )

  const onGridReady = useCallback(
    (event: GridReadyEvent<Row>) => {
      setGridApi(event.api)
      setGridIsReady(true)
      event.api.setGridOption('quickFilterText', quickSearchValueRef.current)
      onGridReadyProp?.(event)
    },
    [onGridReadyProp, quickSearchValueRef],
  )

  const requestColumnsPanel = useCallback(() => {
    setColumnsPanelRequested(true)
  }, [])

  const hasExternalFilter = Boolean(externalFiltersConfig)
  const groupDisplayType =
    agGridProps.groupDisplayType ?? treeDataConfig?.groupDisplayType
  const shouldShowRowGroupOnFirstColumn = Boolean(
    treeDataConfig?.showRowGroupOnFirstColumn && groupDisplayType === 'custom',
  )
  const syncedColumnDefs = useShowRowGroupOnFirstColumn<Row>({
    api: gridApi,
    columnDefs: agGridProps.columnDefs,
    enabled: shouldShowRowGroupOnFirstColumn,
  })

  const isExternalFilterPresent = useCallback(() => {
    const { externalFiltersConfig, externalFiltersSelected } =
      externalFilterStateRef.current
    if (!externalFiltersConfig) return false
    if (externalFiltersConfig.isPresent) {
      return externalFiltersConfig.isPresent(externalFiltersSelected)
    }
    // Presence must follow the allowlist to avoid “phantom filters” coming from
    // legacy/other screens.
    return Object.keys(externalFiltersConfig.fields).some((field) => {
      const values = externalFiltersSelected[field]
      return Array.isArray(values) && values.length > 0
    })
  }, [externalFilterStateRef])

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<Row>) => {
      const { externalFiltersConfig, externalFiltersSelected, gridContext } =
        externalFilterStateRef.current

      if (!externalFiltersConfig) return true
      if (externalFiltersConfig.isRowExempt?.(node, gridContext)) {
        return true
      }

      return Object.entries(externalFiltersSelected).every(
        ([field, selected]) => {
          if (selected.length === 0) return true
          if (
            !Object.prototype.hasOwnProperty.call(
              externalFiltersConfig.fields,
              field,
            )
          ) {
            return true
          }
          const fieldConfig = externalFiltersConfig.fields[field]
          const resolved = fieldConfig?.resolveSelectedValues
            ? fieldConfig.resolveSelectedValues(selected, gridContext)
            : selected

          const resolvedValues = Array.isArray(resolved)
            ? resolved.filter(isValue)
            : []

          if (resolvedValues.length === 0) return true

          if (fieldConfig?.doesPass) {
            return fieldConfig.doesPass(node, resolvedValues, gridContext)
          }

          const rowValues = toValueArray(getValueByPath(node.data, field))
          return matchesValues(rowValues, resolvedValues)
        },
      )
    },
    [externalFilterStateRef],
  )

  const resetGridState = useCallback(() => {
    setQuickSearchValue('')
    resetExternalFilters()

    if (!gridApi) return

    gridApi.setGridOption('quickFilterText', '')
    gridApi.setFilterModel(null)
    gridApi.onFilterChanged()
    gridApi.applyColumnState({
      defaultState: { sort: null, sortIndex: null },
    })
  }, [gridApi, resetExternalFilters])

  const apiContextValue: GridApiContextValue = useMemo(
    () => ({
      gridRef,
      gridIsReady,
      api: gridApi,
      requestColumnsPanel,
    }),
    [gridRef, gridIsReady, gridApi, requestColumnsPanel],
  )

  const stateContextValue: GridStateContextValue = useMemo(
    () => ({
      selectedFilters,
      externalFiltersSelected,
      setExternalFiltersSelected,
      quickSearchValue,
      setQuickSearchValue,
      resetExternalFilters,
      resetGridState,
      storageKey: externalFiltersStorageKey,
    }),
    [
      selectedFilters,
      externalFiltersSelected,
      setExternalFiltersSelected,
      quickSearchValue,
      resetExternalFilters,
      resetGridState,
      externalFiltersStorageKey,
    ],
  )

  const resolvedGridContext = useMemo(() => {
    if (!externalFiltersConfig && !rulesConfig) return gridContext

    const record = isRecord(gridContext) ? gridContext : {}
    const resolvedContext: Record<string, unknown> = { ...record }

    if (externalFiltersConfig) {
      resolvedContext.externalFiltersSelected = externalFiltersSelected
    }
    if (rulesConfig) {
      resolvedContext.__gridRules = rulesConfig
    }

    return resolvedContext as Ctx
  }, [externalFiltersConfig, externalFiltersSelected, gridContext, rulesConfig])

  const resolvedGetRowClass = useMemo(() => {
    if (agGridProps.getRowClass) return agGridProps.getRowClass
    if (!rulesConfig) return undefined
    return createRowClassFn(rulesConfig)
  }, [agGridProps.getRowClass, rulesConfig])

  const autoColumnsSideBar = useMemo(() => {
    if (!columnsPanelRequested || agGridProps.sideBar != null) return undefined
    return sideBarDef()
  }, [agGridProps.sideBar, columnsPanelRequested])

  const resolvedAgGridProps = useMemo(() => {
    const baseGridProps = {
      ...agGridProps,
      columnDefs: syncedColumnDefs,
      sideBar: agGridProps.sideBar ?? autoColumnsSideBar,
      maintainColumnOrder: shouldShowRowGroupOnFirstColumn
        ? true
        : agGridProps.maintainColumnOrder,
      getRowClass: resolvedGetRowClass,
    }

    const treeDataResolved = !treeDataConfig
      ? baseGridProps
      : {
          ...baseGridProps,
          treeData: baseGridProps.treeData ?? true,
          getDataPath: baseGridProps.getDataPath ?? treeDataConfig.getDataPath,
          groupDisplayType:
            baseGridProps.groupDisplayType ?? treeDataConfig.groupDisplayType,
          enableGroupEdit:
            baseGridProps.enableGroupEdit ?? treeDataConfig.enableGroupEdit,
          groupDefaultExpanded:
            baseGridProps.groupDefaultExpanded ??
            treeDataConfig.groupDefaultExpanded,
        }

    return treeDataResolved
  }, [
    agGridProps,
    autoColumnsSideBar,
    resolvedGetRowClass,
    shouldShowRowGroupOnFirstColumn,
    syncedColumnDefs,
    treeDataConfig,
  ])

  const tableContextValue: GridTableContextValue<Row, Ctx> = useMemo(
    () => ({
      agGridProps: resolvedAgGridProps,
      rowDataScopeKey: scopeKey,
      gridContext: resolvedGridContext,
      getContextMenuItems,
      onFilterChanged,
      onGridReady,
      isExternalFilterPresent: hasExternalFilter
        ? isExternalFilterPresent
        : undefined,
      doesExternalFilterPass: hasExternalFilter
        ? doesExternalFilterPass
        : undefined,
      hasExternalFilter,
      aggregationConfig,
      contextRefreshConfig,
    }),
    [
      resolvedAgGridProps,
      scopeKey,
      resolvedGridContext,
      getContextMenuItems,
      onFilterChanged,
      onGridReady,
      isExternalFilterPresent,
      doesExternalFilterPass,
      hasExternalFilter,
      aggregationConfig,
      contextRefreshConfig,
    ],
  )

  return (
    <GridApiContext.Provider value={apiContextValue}>
      <GridStateContext.Provider value={stateContextValue}>
        {viewsEnabled && viewsConfig ? (
          <GridViewsProvider
            config={viewsConfig}
            scope={scope}
            api={gridApi}
            gridIsReady={gridIsReady}
            columnDefsReady={columnDefsReady}
            columnDefs={syncedColumnDefs}
            columnStateConfig={columnStateConfig}
            externalFiltersConfig={externalFiltersConfig}
            externalFiltersSelected={externalFiltersSelected}
            setExternalFiltersSelected={setExternalFiltersSelected}
            tableContextValue={tableContextValue}
            showRowGroupOnFirstColumn={shouldShowRowGroupOnFirstColumn}
          >
            {children}
          </GridViewsProvider>
        ) : (
          <GridViewsContext.Provider value={null}>
            <GridTableContext.Provider value={tableContextValue}>
              {children}
            </GridTableContext.Provider>
          </GridViewsContext.Provider>
        )}
      </GridStateContext.Provider>
    </GridApiContext.Provider>
  )
}

export function useGridApi() {
  const context = useContext(GridApiContext)
  if (!context) {
    throw new Error('useGridApi must be used within Grid.Root')
  }
  return context
}

export function useGridState() {
  const context = useContext(GridStateContext)
  if (!context) {
    throw new Error('useGridState must be used within Grid.Root')
  }
  return context
}

export function useGridViews() {
  const context = useContext(GridViewsContext)
  if (context === undefined) {
    throw new Error('useGridViews must be used within Grid.Root')
  }
  return context
}

export function useGridTable<Row, Ctx>() {
  const context = useContext(GridTableContext)
  if (!context) {
    throw new Error('useGridTable must be used within Grid.Root')
  }
  return context as GridTableContextValue<Row, Ctx>
}

export function useGrid<Row, Ctx>() {
  const api = useGridApi()
  const state = useGridState()
  const table = useGridTable<Row, Ctx>()
  return {
    ...api,
    ...state,
    ...table,
  } as GridContextValue<Row, Ctx>
}

export function useGridSelectionCount() {
  const { api, gridIsReady } = useGridApi()

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!api || !gridIsReady) return NOOP_UNSUBSCRIBE

      api.addEventListener('selectionChanged', onStoreChange)
      return () => {
        try {
          if (api.isDestroyed?.()) return
          api.removeEventListener('selectionChanged', onStoreChange)
        } catch {
          // grid may be torn down during fast navigation
        }
      }
    },
    [api, gridIsReady],
  )

  return useSyncExternalStore(
    subscribe,
    () => {
      if (!api || !gridIsReady) return 0
      const selectedRows =
        typeof api.getSelectedRows === 'function' ? api.getSelectedRows() : []
      return Array.isArray(selectedRows) ? selectedRows.length : 0
    },
    () => 0,
  )
}
